// Keyboard pieces the claims queue and the review screen share (#11422).
//
// A mailroom reviewer works ~800 documents a day, and the one who wants the
// keyboard turns keyboard mode on in the `?` overlay. Until then
// the screens answer only `?`, Esc and the focused control's own keys, and
// no key hint shows anywhere.

import React, { useEffect } from "react";
import { Dialog } from "@mui/material";
import {
  isApplePlatform,
  paletteShortcutLabel,
} from "../RMS/ViewStrip/platformKeys";
import { useKeyboardMode } from "./keyboardMode";
import ClaimsSwitch from "./ClaimsSwitch";

/** True when a key press belongs to a text field, not to a shortcut. */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable
  );
};

/**
 * True when Enter or Space on this target should press the focused control
 * (a button, link, checkbox or tab) rather than fire a shortcut: a reviewer
 * who tabbed to Cancel and pressed Enter meant Cancel. A reason radio is not
 * one of these, so Enter on a just-clicked reason still confirms it.
 */
export const isActivatingControl = (target: EventTarget | null): boolean => {
  const el = (target as HTMLElement | null)?.closest?.(
    'button, a[href], [role="checkbox"], [role="tab"], [role="menuitem"], [role="switch"]'
  );
  return Boolean(el) && el?.getAttribute("role") !== "radio";
};

/** True when an open menu, popover or dialog has the keyboard: a letter under
 * it decides nothing. */
export const isMenuTarget = (target: EventTarget | null): boolean =>
  Boolean(
    (target as HTMLElement | null)?.closest?.(
      '[role="menu"], [role="menuitem"], [role="listbox"], [role="option"], .MuiModal-root'
    )
  );

/** True when a modifier other than Shift is held: the browser's, not ours. */
export const hasCommandModifier = (event: KeyboardEvent): boolean =>
  event.metaKey || event.ctrlKey || event.altKey;

/** A key, drawn the way a keycap is; nothing while keyboard mode is off. */
export const KeyHint: React.FC<{
  children: React.ReactNode;
  tone?: "on-ink";
}> = ({ children, tone }) => {
  const [keyboard] = useKeyboardMode();
  if (!keyboard) return null;
  return (
    <kbd
      // Hidden from the accessible name: "Accept A" is noise to a screen
      // reader. The key is listed, named, in the `?` overlay.
      aria-hidden
      style={{
        marginLeft: 8,
        padding: "0 5px",
        minWidth: 18,
        display: "inline-block",
        textAlign: "center",
        borderRadius: 4,
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-micro)",
        lineHeight: "18px",
        border: `1px solid ${tone === "on-ink" ? "color-mix(in srgb, var(--fp-surface) 35%, transparent)" : "var(--fp-border-strong)"}`,
        color: tone === "on-ink" ? "var(--fp-surface)" : "var(--fp-ink-3)",
        background: "transparent",
      }}
    >
      {children}
    </kbd>
  );
};

export interface Shortcut {
  keys: string[];
  does: string;
}

/** The view strip's keys, the RMS page's, wherever the strip is on screen.
 * ⌘1-9 only where there is a ⌘: Ctrl+1-9 is the browser's. */
export const stripShortcuts = (): Shortcut[] => [
  { keys: [paletteShortcutLabel()], does: "Change view" },
  ...(isApplePlatform()
    ? [{ keys: ["⌘1-9"], does: "Open that pinned view" }]
    : []),
];

/**
 * The account menu's "Keyboard shortcuts" (round 18): the claims screens
 * open their own list when it is asked for. A window event, since the menu
 * lives in the nav and cannot reach a page's state.
 */
const OPEN_SHORTCUTS_EVENT = "claims-open-shortcuts";

export const requestClaimsShortcuts = (): void => {
  window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT));
};

/** Opens this screen's shortcuts list when the account menu asks for it. */
export const useShortcutsRequest = (open: () => void): void => {
  useEffect(() => {
    window.addEventListener(OPEN_SHORTCUTS_EVENT, open);
    return () => window.removeEventListener(OPEN_SHORTCUTS_EVENT, open);
  }, [open]);
};

/**
 * The `?` overlay: the keyboard-mode switch, then every key on this screen
 * and what it does. The list is shown either way, so a reviewer can read
 * what turning it on would give them.
 */
export const ShortcutsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  shortcuts: Shortcut[];
}> = ({ open, onClose, shortcuts }) => {
  const [keyboard, setKeyboard] = useKeyboardMode();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="claims-shortcuts-title"
    >
      <div
        style={{
          padding: "18px 20px",
          fontFamily: "var(--fp-font-ui)",
          color: "var(--fp-ink)",
        }}
      >
        <h2
          id="claims-shortcuts-title"
          style={{
            margin: "0 0 14px",
            fontSize: "var(--fp-t-lede)",
            fontWeight: "var(--fp-w-head)",
          }}
        >
          Keyboard shortcuts
        </h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 12px",
            marginBottom: 14,
            borderRadius: 9,
            background: "var(--fp-band)",
            border: "1px solid var(--fp-border)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: "var(--fp-t-body)",
                fontWeight: "var(--fp-w-strong)",
              }}
            >
              Keyboard mode
            </div>
            <div
              style={{ fontSize: "var(--fp-t-meta)", color: "var(--fp-ink-2)" }}
            >
              {keyboard
                ? "On for the queue and the review screen."
                : "Off. Only ? and Esc answer."}
            </div>
          </div>
          <ClaimsSwitch
            on={keyboard}
            onChange={setKeyboard}
            label="Keyboard mode"
          />
        </div>
        <dl
          style={{
            margin: 0,
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: 16,
            rowGap: 6,
            fontSize: "var(--fp-t-body)",
            opacity: keyboard ? 1 : 0.6,
          }}
        >
          {shortcuts.map((shortcut) => (
            <React.Fragment key={shortcut.does}>
              <dt style={{ whiteSpace: "nowrap" }}>
                {shortcut.keys.map((key) => (
                  <kbd
                    key={key}
                    style={{
                      marginRight: 4,
                      padding: "0 5px",
                      borderRadius: 4,
                      border: "1px solid var(--fp-border-strong)",
                      fontFamily: "var(--fp-font-ui)",
                      fontSize: "var(--fp-t-meta)",
                    }}
                  >
                    {key}
                  </kbd>
                ))}
              </dt>
              <dd style={{ margin: 0, color: "var(--fp-ink-2)" }}>
                {shortcut.does}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </Dialog>
  );
};
